import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { customAdminApi } from '../services/api';
import type { CustomInstanceListParams } from '../types/custom';
import { operationInProgress } from '../utils/customUi';

export const customInstanceKeys = {
  all: ['custom-instances'] as const,
  lists: () => [...customInstanceKeys.all, 'list'] as const,
  list: (params: CustomInstanceListParams) => [...customInstanceKeys.lists(), params] as const,
  details: () => [...customInstanceKeys.all, 'detail'] as const,
  detail: (instanceId: string) => [...customInstanceKeys.details(), instanceId] as const,
};

export async function invalidateCustomInstanceQueries(
  queryClient: QueryClient,
  instanceId?: string,
): Promise<void> {
  const invalidations: Array<Promise<unknown>> = [
    queryClient.invalidateQueries({ queryKey: customInstanceKeys.lists() }),
  ];
  if (instanceId) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: customInstanceKeys.detail(instanceId) }));
  }
  await Promise.all(invalidations);
}

export function useCustomInstances(params: CustomInstanceListParams) {
  return useQuery({
    queryKey: customInstanceKeys.list(params),
    queryFn: () => customAdminApi.listInstances(params),
    refetchInterval: query => query.state.data?.instances.some(instance => (
      ['preparing', 'creating', 'starting', 'stopping', 'deleting'].includes(instance.actualState)
      || operationInProgress(instance.lifecycleOperation?.phase)
    )) ? 2_000 : 10_000,
  });
}

export function useCustomInstance(instanceId: string | undefined) {
  return useQuery({
    queryKey: customInstanceKeys.detail(instanceId || ''),
    queryFn: () => customAdminApi.getInstance(instanceId!),
    enabled: Boolean(instanceId),
    refetchInterval: query => {
      const instance = query.state.data?.instance;
      if (!instance) return false;
      return ['preparing', 'creating', 'starting', 'stopping', 'deleting'].includes(instance.observed.actualState)
        || operationInProgress(instance.observed.lifecycleOperation?.phase)
        ? 2_000
        : 10_000;
    },
  });
}

export function useCustomInstanceSocketInvalidation(instanceId?: string): void {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;
    const refresh = (payload: { instanceId?: string }) => {
      if (!payload?.instanceId) return;
      void queryClient.invalidateQueries({ queryKey: customInstanceKeys.lists() });
      if (!instanceId || payload.instanceId === instanceId) {
        void queryClient.invalidateQueries({ queryKey: customInstanceKeys.detail(payload.instanceId) });
      }
    };
    socket.on('custom:instance:state', refresh);
    return () => { socket.off('custom:instance:state', refresh); };
  }, [instanceId, queryClient, socket]);
}
